import React, { FC, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Divider } from "@mui/material";
import { Link } from "react-router-dom";
import PhBellSvg from "../../../assets/svg-fc/PhBellSvg";
import PhCaretRightSvg from "../../../assets/svg-fc/PhCaretRightSvg";
import PhListSvg from "../../../assets/svg-fc/PhListSvg";
import { iHeaderCrumb } from "../../reusable-components/side-nav/headerTrail";
import NotificationDropdown from "./notification-dropdown/NotificationDropdown";
import "./header.scss";

interface iHeaderProps {
	isNotificationReceived: boolean;
	notificationOpen: boolean;
	notificationHandleClick: (event: React.MouseEvent<HTMLElement>) => void;
	notificationAnchorEl: HTMLElement | null;
	notificationHandleClose: () => void;
	avataOpen: boolean;
	handleClick: (event: React.MouseEvent<HTMLElement>) => void;
	anchorEl: HTMLElement | null;
	handleClose: () => void;
	avatarName: string;
	avatarEmail: string;
	avatarSrc?: string;
	buttonLabel: string;
	logoutHandler: () => void;
	/** Below the desktop width: a menu button opens the nav drawer. */
	compact: boolean;
	onOpenMenu: () => void;
	/** The nav has something unread — a dot on the menu button while the nav is a drawer. */
	notificationBadge?: boolean;
	/** Maktab's interface size (CSS zoom). */
	zoom?: number;
	/** The breadcrumb, as on admin — section › page (› item). */
	trail: iHeaderCrumb[];
}

const Header: FC<iHeaderProps> = ({
	isNotificationReceived,
	notificationOpen,
	notificationHandleClick,
	notificationAnchorEl,
	notificationHandleClose,
	avataOpen,
	handleClick,
	anchorEl,
	handleClose,
	avatarName,
	avatarEmail,
	avatarSrc,
	buttonLabel,
	logoutHandler,
	compact,
	onOpenMenu,
	notificationBadge,
	zoom = 1,
	trail,
}) => {
	const [failedSrc, setFailedSrc] = useState<string | null>(null);
	const showLogo = Boolean(avatarSrc) && failedSrc !== avatarSrc;
	const initials =
		avatarName
			.split(" ")
			.filter(Boolean)
			.slice(0, 2)
			.map((word) => word[0]?.toUpperCase())
			.join("") || avatarEmail.charAt(0).toUpperCase();

	return (
		<Box component="header" className={`header ${compact ? "header--compact" : ""}`} sx={{ zoom }}>
			<Box className="headerHold">
				{compact && (
					<button
						type="button"
						className="headerMenuButton"
						onClick={onOpenMenu}
						aria-label="Open menu"
						title="Open menu"
					>
						<PhListSvg size={19} />
						{notificationBadge && <span className="headerMenuDot" aria-hidden />}
					</button>
				)}
				{trail.length > 0 && (
					<nav aria-label="breadcrumb" className="headerTrail">
						<ol>
							{trail.map((crumb, index) => {
								const isPage = index === trail.length - 1;
								return (
									<li key={`${crumb.label}-${index}`}>
										{index > 0 && (
											<span className="headerTrailCaret" aria-hidden>
												<PhCaretRightSvg size={12} />
											</span>
										)}
										{isPage || !crumb.href ? (
											<span
												className={isPage ? "headerTrailPage" : "headerTrailLink"}
												aria-current={isPage ? "page" : undefined}
												title={crumb.label}
											>
												{crumb.label}
											</span>
										) : (
											<Link to={crumb.href} className="headerTrailLink" title={crumb.label}>
												{crumb.label}
											</Link>
										)}
									</li>
								);
							})}
						</ol>
					</nav>
				)}

				<Box className="header-right-side">
					<Box className="notificationBox">
						<Box
							component="button"
							type="button"
							aria-label={isNotificationReceived ? "Notifications — unread" : "Notifications"}
							aria-haspopup="true"
							aria-expanded={notificationOpen ? "true" : undefined}
							className="notificationIcon"
							onClick={notificationHandleClick}
						>
							<PhBellSvg size={18} />
							{isNotificationReceived && <span className="notificationDot" />}
						</Box>
						<NotificationDropdown
							notificationOpen={notificationOpen}
							notificationAnchorEl={notificationAnchorEl}
							notificationHandleClose={notificationHandleClose}
						/>
					</Box>
					<Box className="notification_profile">
						<Box className="avtarHold">
							<Button
								id="fade-button"
								aria-controls={avataOpen ? "fade-menu" : undefined}
								aria-haspopup="true"
								aria-expanded={avataOpen ? "true" : undefined}
								onClick={handleClick}
							>
								{showLogo ? (
									<span className="profileInitials profileLogo">
										<img
											src={avatarSrc}
											alt={avatarName || "Profile"}
											onError={() => setFailedSrc(avatarSrc ?? null)}
										/>
									</span>
								) : (
									<span className="profileInitials" aria-label={avatarName || "Profile"}>
										{initials}
									</span>
								)}
							</Button>
							<Menu
								id="fade-menu"
								anchorEl={anchorEl}
								open={avataOpen}
								onClose={handleClose}
								className="avatarDropdown"
								slotProps={{
									list: {
										"aria-labelledby": "fade-button",
									},
								}}
							>
								<MenuItem>
									<span>{avatarName}</span>
									<p>{avatarEmail}</p>
								</MenuItem>
								<Divider />
								<MenuItem component="button" type="button" onClick={logoutHandler}>
									{buttonLabel}
								</MenuItem>
							</Menu>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
export default Header;
